import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { cn } from '@/lib/utils'
import { useMemo } from 'react'

interface TimePickerProps {
  value?: Date
  onChange: (date: Date | undefined) => void
  disabled?: boolean
  className?: string
  error?: string
}

export function TimePicker({
  value,
  onChange,
  disabled = false,
  className,
  error,
}: Readonly<TimePickerProps>) {
  const hours = [
    { value: '12', label: '12' },
    ...Array.from({ length: 11 }, (_, i) => {
      const hour = i + 1
      return {
        value: hour.toString(),
        label: hour.toString().padStart(2, '0'),
      }
    }),
  ]

  // Generate minutes (0-59)
  const minutes = Array.from({ length: 60 }, (_, i) => {
    return {
      value: i.toString(),
      label: i.toString().padStart(2, '0'),
    }
  })

  const periods = [
    { value: 'AM', label: 'AM' },
    { value: 'PM', label: 'PM' },
  ]

  const currentHour = useMemo(() => {
    if (!value) return '12'
    const hour = value.getHours()
    const hour12 = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour
    return hour12.toString()
  }, [value])

  const currentMinute = useMemo(() => {
    if (!value) return '0'
    const minute = value.getMinutes()
    return minute.toString()
  }, [value])

  const currentPeriod = useMemo(() => {
    if (!value) return 'AM'
    return value.getHours() >= 12 ? 'PM' : 'AM'
  }, [value])

  const handleTimeChange = (
    hour?: string,
    minute?: string,
    period?: string
  ) => {
    const newHour = hour ?? currentHour
    const newMinute = minute ?? currentMinute
    const newPeriod = period ?? currentPeriod

    let hour24 = Number.parseInt(newHour)
    if (newPeriod === 'PM' && hour24 !== 12) {
      hour24 += 12
    } else if (newPeriod === 'AM' && hour24 === 12) {
      hour24 = 0
    }

    const newDate = value ? new Date(value) : new Date()
    newDate.setHours(hour24)
    newDate.setMinutes(Number.parseInt(newMinute))
    newDate.setSeconds(0)
    newDate.setMilliseconds(0)

    onChange(newDate)
  }

  if (!value) {
    const defaultDate = new Date()
    defaultDate.setHours(0, 0, 0, 0)
    onChange(defaultDate)
  }

  return (
    <div className={cn('flex gap-2', className)}>
      <div className="">
        <Select
          value={currentHour}
          onValueChange={hour => handleTimeChange(hour)}
          disabled={disabled}
        >
          <SelectTrigger
            size="lg"
            className={cn(
              'h-11 border-ss-neutral-100',
              error && 'border-ss-critical-200'
            )}
          >
            <SelectValue placeholder="HH" />
          </SelectTrigger>
          <SelectContent>
            {hours.map(hour => (
              <SelectItem key={hour.value} value={hour.value}>
                {hour.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="flex items-center">
        <Label className="text-ss-neutral-600">:</Label>
      </div>

      <div className="">
        <Select
          value={currentMinute}
          onValueChange={minute => handleTimeChange(undefined, minute)}
          disabled={disabled}
        >
          <SelectTrigger
            size="lg"
            className={cn(
              'h-11 border-ss-neutral-100',
              error && 'border-ss-critical-200'
            )}
          >
            <SelectValue placeholder="MM" />
          </SelectTrigger>
          <SelectContent>
            {minutes.map(minute => (
              <SelectItem key={minute.value} value={minute.value}>
                {minute.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="flex-1">
        <Select
          value={currentPeriod}
          onValueChange={period =>
            handleTimeChange(undefined, undefined, period)
          }
          disabled={disabled}
        >
          <SelectTrigger
            size="lg"
            className={cn(
              'h-11 border-ss-neutral-100',
              error && 'border-ss-critical-200'
            )}
          >
            <SelectValue placeholder="AM/PM" />
          </SelectTrigger>
          <SelectContent>
            {periods.map(period => (
              <SelectItem key={period.value} value={period.value}>
                {period.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  )
}
